import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { FacetSelected } from '../models/facet-selected-model';
import { MetadataProp } from '../models/metadata-prop-model';
import { QueryViewModel } from '../models/query-view-model';
import { SearchComboItemModel } from '../models/search-combo-item-model';
import { SearchComboModel } from '../models/search-combo-model';
import { SearchFilter } from '../models/search-filter-model';
import { SearchRequestModel } from '../models/search-request-model';
import { SortDetails } from '../models/sort-details-model';
import { MetadataService } from './metadata.service';
import { ConstantsService } from './constants.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class QueryStateService {

  private formData?: any;
  private formDataString = '';
  private queryString: string = '';
  private queryView: QueryViewModel = { allProps: [], anyProps: [], options: [] };
  private mdItems?: SearchComboModel[];
  private facetitems: SearchFilter[] = [];
  private queryFilters: SearchFilter[] = []; //Original Search Filters And
  private queryAnyFilters: SearchFilter[] = []; //Original Search Filters And
  private sortBy?: string = '';
  private sortDir?: number = 1;
  private searchRequest: SearchRequestModel = { q: '', filters: [], legacyLibFilter: true, mainLibFilter: true, content: true }; //Search Request Based on the Form Data

  private querySubject = new BehaviorSubject<QueryViewModel>({});
  qData$ = this.querySubject.asObservable();

  constructor(private mdSvc: MetadataService, private constants: ConstantsService) {
    this.mdItems = this.mdSvc.mdItems;
  }

  //Behavior subject
  setqData(data: QueryViewModel) {
    this.querySubject.next(data);
  }

  getqData(): QueryViewModel {
    return this.querySubject.value;
  }

  //Used to return the columns for the table View based on the search
  getSearchFilters() {
    return this.queryFilters.concat(this.queryAnyFilters);
  }

  getQueryString() {
    return this.queryString;
  }

  updateQueryString(value: string) {
    this.queryString = value;
  }

  getFormData() {
    return this.formData;
  }

  getFormDataString() {
    return this.formDataString;
  }

  updateFormData(value: FormGroup) {
    // console.log('up fd new value: ' + JSON.stringify(value) );

    this.formData = value;
    this.formDataString = JSON.stringify(this.formData);
    this.parseSearchForm();
  }

  //Used to display details for saved search
  parseToQueryView(fd: any): QueryViewModel {

    let qv: QueryViewModel = {};

    qv.allProps = [];
    qv.anyProps = [];
    qv.options = [];

    qv.keywords = fd.keywords;

    let allProps = fd[this.constants.property_operator_all];
    let anyProps = fd[this.constants.property_operator_any];

    if (anyProps) {
      qv.anyProps = this.processQueryViewProperties(anyProps);
    }

    if (allProps) {
      qv.allProps = this.processQueryViewProperties(allProps);
    }

    if (fd.mainLibFilter) {
      qv.options.push("Main Library");
    }

    if (fd.legacyLibFilter) {
      qv.options.push("Legacy Library");
    }

    return qv;
  }

  processQueryViewProperties(searchProps: any): any[] {
    let propsArray: any[] = [];

    searchProps.forEach((i: SearchComboItemModel) => {
      if (i.propertyItem != '') {
        let p = this.mdItems?.find((m) => m.id == i.propertyItem) as MetadataProp;
        if (p) {
          propsArray.push({
            displayName: p.displayName, keywords: i.keywords,
            isDate: i.isDate, startDate: i.startDate, endDate: i.endDate,
            startTime: i.startTime, endTime: i.endTime,
            dateOperator: i.dateOperator, textOperator: i.textOperator
          });
        }
      }
    });

    return propsArray;
  }

  getQueryView() {
    this.queryView = this.parseToQueryView(this.formData);
    this.setqData(this.queryView);
    return this.queryView;
  }

  updateMetadataProps(items: MetadataProp[]) {
    this.mdItems = items;
  }

  getMetadataProps() {
    return this.mdItems;
  }

  clearSelectedFacets() {
    this.facetitems = [];
  }

  updateSelectedFacets(item: FacetSelected) {
    // Get all the items for the same facet name
    this.facetitems = this.facetitems.filter(
      function (value, index, arr) {
        return value.field != item.field
      });

    // Add the items in the new items list
    item.values.forEach((i) => {
      let sf: SearchFilter = {
        field: item.field,
        value: i,
        operator: this.constants.text_operator_equals
      };
      this.facetitems.push(sf);
    });
  }

  updateSelectedSort(sortItem: SortDetails) {
    this.sortBy = sortItem.field;
    this.sortDir = sortItem.order;
  }

  resetSort() {
    this.sortBy = '';
    this.sortDir = 1;
  }

  getSortBy(): string {
    return this.sortBy ?? '';
  }

  getSortDir(): number {
    return this.sortDir ?? 1;
  }

  // Returns the Original Search request with original Filters based on the FormData
  getSearchRequest(): SearchRequestModel {
    return this.searchRequest;
  }

  // returns a Search request with the selected facets.
  getUpdatedSearchRequest(): SearchRequestModel {
    // Form the Search Request using the original Query and the facets
    let combinedFilters = this.queryFilters.concat(this.facetitems);
    this.searchRequest.filters = combinedFilters;
    this.searchRequest.sort = this.sortBy;
    this.searchRequest.sortDirection = this.sortDir;
    return this.searchRequest;
  }

  updateSkipCount(count: number) {
    this.searchRequest.skip = count;
  }

  async parseSearchForm() {
    let q = '';

    // console.log("Form Datap2: " + JSON.stringify(this.formData));
    // TODO: Check if formData null and redirect if not????
    if (this.formData) {
      let k = this.formData['keywords'];
      if (k) {
        q = k;
      }

      let anyProps = this.formData[this.constants.property_operator_any];
      let allProps = this.formData[this.constants.property_operator_all];

      this.searchRequest.q = q;
      this.searchRequest.filters = this.parseFromProps(allProps);
      this.searchRequest.anyFilters = this.parseFromProps(anyProps);
      this.searchRequest.mainLibFilter = this.formData['mainLibFilter'];
      this.searchRequest.legacyLibFilter = this.formData['legacyLibFilter'];
      this.queryFilters = this.searchRequest.filters;
      this.queryAnyFilters = this.searchRequest.anyFilters;

      // console.log("sr: " + JSON.stringify(this.searchRequest));
    }
  }

  parseFromProps(props: any): SearchFilter[] {
    let f: SearchFilter[] = [];

    // Parse the property Filters and add to the query request
    props.forEach((i: SearchComboItemModel) => {
      if (i.propertyItem != '') {
        //Lookup the CogSearchProperty
        let p = this.mdItems?.find((m) => m.id == i.propertyItem) as MetadataProp;

        // Test if the keywords is empty or if the property item is empty
        if (p && p.cogSearchKey) {
          if (i.isDate) {
            if (i.startDate && i.dateOperator) { //Validate if the date values exists.
              f.push(this.parseDateFilter(p.cogSearchKey, i));
            }
          }
          else {
            i.keywords = i.keywords?.trim();
            if (i.keywords) {
              f.push({ field: p.cogSearchKey, value: i.keywords, operator: i.textOperator });
            }
          }
        }
      }
    });

    return f;
  }

  private splitPhrases(data: string): string[] {
    let matches: string[] = [];
    let regex = /"([^"]+)"|([^ ]+)/g;
    let match;

    while ((match = regex.exec(data)) !== null) {
      // matches.push(match[1] || match[2]);
      matches.push(match[0]);
    }

    return matches;
  }

  private splitOperators(data: string): string[] {
    let matches: string[] = [];
    let regex = /("[^"]+")|([^ ]+)|and|or/gi;
    let match;

    while ((match = regex.exec(data)) != null) {
      matches.push(match[0]);
    }

    return matches;
  }

  // Used by the Adv Search Properties
  parseDateFilter(fieldName: string, item: SearchComboItemModel): SearchFilter {
    let filter: SearchFilter = { field: '' };
    let dateFilter = '';

    if (item.dateOperator == 'between' && !item.endDate) {
      item.dateOperator = 'starts';
    }

    const hasTimes = item.startTime || item.endTime;
    let startValue = item.startDate;
    let endValue = item.endDate;

    if (fieldName === 'DateAdded' && hasTimes) {
      fieldName = 'DateAddedTimestamp';

      if (item.startTime) {
        let st = this.convertTo24h(item.startTime);
        startValue = item.startDate + ' ' + st;
        // startValue = item.startDate + ' ' + item.startTime;
        // console.log("start time: " + startValue);
      }

      if (item.endTime) {
        let et = this.convertTo24h(item.endTime);
        endValue = item.endDate + ' ' + et;
        // endValue = item.endDate + ' ' + item.endTime;
        // console.log("end time: " + endValue);
      }
    }

    // Validate Start/End Dates
    if (item.dateOperator == 'between') {
      dateFilter = "(" + fieldName + " ge '" + startValue + "' and " + fieldName + " le '" + endValue + "')";
    }
    else if (item.dateOperator == 'starts') {

      dateFilter = "(" + fieldName + " ge '" + startValue + "')";
    }
    else if (item.dateOperator == 'ends') {
      dateFilter = "(" + fieldName + " le '" + startValue + "')";
    }
    else if (item.dateOperator == 'equals') {
      dateFilter = "(" + fieldName + " eq '" + startValue + "')";
    }

    filter.field = fieldName;
    filter.value = dateFilter;

    return filter;
  }

  // Format the Display when a new Facet is added.
  displayFilters(data: SearchFilter[]): string {
    let filterString = '';

    data.forEach((f) => {
      filterString = filterString + ' ' + f.field + ' =  ' + '"' + f.value + '", ';
    });

    // return JSON.stringify(data);
    filterString = filterString.substring(0, filterString.length - 2);
    return filterString;
  }

  convertTo24h(time: string): string {
    const m = time.trim().match(/^(\d{1,2}):([0-5]\d)\s*([AaPp][Mm])$/);
    if (!m) throw new Error(`Invalid time format: "${time}"`);
    let hour = parseInt(m[1], 10);
    if (hour < 1 || hour > 12) throw new Error(`Hour out of range: ${hour}`);
    const minutes = m[2];
    const meridiem = m[3].toUpperCase();

    // 12 AM -> 00; 12 PM -> 12
    if (hour === 12) hour = 0;
    if (meridiem === 'PM') hour += 12;

    return `${hour.toString().padStart(2, '0')}:${minutes}`;

  }
}
